import {
  damerauLevenshtein,
  givenNameKeys,
  isObviousBowlerMatch,
  searchOrgBowlersForTeamCreate,
} from "./bowlerMatch";
import { splitBowlerName } from "./bowlerName";
import {
  nameKey,
  normalizeMatchName,
  normalizeUsbcId,
} from "./bowlerMatchKeys";

const REASON_ORDER = ["Same USBC ID", "Same name", "Similar name"];
const FUZZY_BUCKET_LIMIT = 60;

const firstOf = (bowler) =>
  normalizeMatchName(splitBowlerName(bowler?.name).firstName);

const lastOf = (bowler) =>
  normalizeMatchName(splitBowlerName(bowler?.name).lastName);

const sharesNickname = (leftFirst, rightFirst) => {
  if (!leftFirst || !rightFirst || leftFirst === rightFirst) return false;
  const rightKeys = new Set(givenNameKeys(rightFirst));
  return givenNameKeys(leftFirst).some((key) => rightKeys.has(key));
};

const createdTime = (bowler) => Date.parse(bowler?.created_at || "") || 0;

export const obviousOrgBowlerMatches = (form, bowlers) =>
  searchOrgBowlersForTeamCreate({
    query: form?.name,
    usbcID: form?.usbcID,
    email: form?.email,
    phone: form?.phone,
    orgBowlers: bowlers,
    limit: 5,
  }).filter((bowler) => isObviousBowlerMatch(bowler, form?.name));

export const findDuplicateBowlerGroups = (bowlers) => {
  const people = (bowlers || []).filter((bowler) => bowler?.id);
  const parent = new Map(people.map((bowler) => [bowler.id, bowler.id]));
  const reasonByRoot = new Map();

  const find = (id) => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root);
    let current = id;
    while (current !== root) {
      const next = parent.get(current);
      parent.set(current, root);
      current = next;
    }
    return root;
  };

  const unite = (leftId, rightId, reason) => {
    if (!leftId || !rightId || leftId === rightId) return;
    const left = find(leftId);
    const right = find(rightId);
    const reasons = new Set([
      ...(reasonByRoot.get(left) || []),
      ...(reasonByRoot.get(right) || []),
      reason,
    ]);
    if (left !== right) parent.set(right, left);
    reasonByRoot.set(left, reasons);
    if (left !== right) reasonByRoot.delete(right);
  };

  const grouped = (keyFor, reason) => {
    const buckets = new Map();
    people.forEach((bowler) => {
      const key = keyFor(bowler);
      if (!key) return;
      const list = buckets.get(key) || [];
      list.push(bowler);
      buckets.set(key, list);
    });
    buckets.forEach((list) => {
      for (let index = 1; index < list.length; index += 1) {
        unite(list[0].id, list[index].id, reason);
      }
    });
  };

  grouped((bowler) => normalizeUsbcId(bowler.usbcID || bowler.usbcId), "Same USBC ID");
  grouped((bowler) => nameKey(bowler.name), "Same name");
  grouped((bowler) => {
    const last = lastOf(bowler);
    const first = firstOf(bowler);
    if (!last || !first) return "";
    return `${last}\t${first}`;
  }, "Same name");

  const pairSimilar = (keyFor, compare) => {
    const buckets = new Map();
    people.forEach((bowler) => {
      const key = keyFor(bowler);
      if (!key) return;
      const list = buckets.get(key) || [];
      list.push(bowler);
      buckets.set(key, list);
    });
    buckets.forEach((list) => {
      if (list.length < 2 || list.length > FUZZY_BUCKET_LIMIT) return;
      for (let left = 0; left < list.length; left += 1) {
        for (let right = left + 1; right < list.length; right += 1) {
          if (compare(list[left], list[right])) {
            unite(list[left].id, list[right].id, "Similar name");
          }
        }
      }
    });
  };

  pairSimilar(lastOf, (left, right) => {
    const leftFirst = firstOf(left);
    const rightFirst = firstOf(right);
    if (!leftFirst || !rightFirst) return false;
    if (sharesNickname(leftFirst, rightFirst)) return true;
    return (
      leftFirst !== rightFirst &&
      damerauLevenshtein(leftFirst, rightFirst) <= 1
    );
  });

  pairSimilar(firstOf, (left, right) => {
    const leftLast = lastOf(left);
    const rightLast = lastOf(right);
    if (!leftLast || !rightLast || leftLast === rightLast) return false;
    return damerauLevenshtein(leftLast, rightLast) <= 1;
  });

  const groups = new Map();
  people.forEach((bowler) => {
    const root = find(bowler.id);
    const list = groups.get(root) || [];
    list.push(bowler);
    groups.set(root, list);
  });

  return Array.from(groups.entries())
    .filter(([, members]) => members.length > 1)
    .map(([root, members]) => {
      const sorted = [...members].sort((left, right) => {
        const byCreated = createdTime(left) - createdTime(right);
        if (byCreated !== 0) return byCreated;
        return String(left.name || "").localeCompare(String(right.name || ""));
      });
      const reasons = Array.from(reasonByRoot.get(root) || []).sort(
        (left, right) => REASON_ORDER.indexOf(left) - REASON_ORDER.indexOf(right)
      );
      return {
        id: sorted.map((bowler) => bowler.id).join(":"),
        reasons,
        bowlers: sorted,
      };
    })
    .sort((left, right) =>
      String(left.bowlers[0]?.name || "").localeCompare(
        String(right.bowlers[0]?.name || "")
      )
    );
};
